import { ReportingGroupFilterSwitcher } from "#batteries-included-components/Filters/ReportingGroupFilterSwitcher";
import { CalculatorResultsSummaryPanel } from "#batteries-included-components/Panels/MetricTilePanels/CalculatorResultsSummaryPanel";
import { useListCalculatorResults } from "#hooks/adapters/useCalculatorResults";
import { useCalculatorResultsMeasurementHeaders } from "#hooks/tables/useMeasurementHeaders";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { linkToFacilityEmissionRecordDetail } from "#routes/organization/facilities/[facilityId]/emissions/[emissionPeriod]/detail";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { EquipmentUpdateStatusBanner } from "#src/components/Organization/Facilities/Detail/Equipment/EquipmentUpdateStatusBanner";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { linkToEquipmentEmissionRecordDetail } from "#src/routes/organization/equipment/[equipmentId]/emissions/[emissionPeriod]/detail";
import { FacilityUpdateStatusBanner } from "#src/routes/organization/facilities/[facilityId]/FacilityUpdateStatusBanner";
import { getIntervalInMonthsFormatted, hydrateDateRange } from "#utils/date";
import {
  DataTablePanel,
  DateSelectorInput,
  FilterPanel,
  useFilters,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  CalculatorResultType,
  SortDirection,
} from "@validereinc/domain";
import { DateFormats, monthFormatter } from "@validereinc/utilities";
import parse from "date-fns/parse";
import { isNil } from "es-toolkit/compat";
import React from "react";

export const FILTER_CONFIG = {
  date: {
    name: "date_range",
  },
  reportingScenario: {
    name: "reporting_scenario",
  },
} as const;

export const EmissionsRecordsTab = ({
  assetType,
  assetId,
}: {
  assetType: AssetTypeType;
  assetId?: string;
}) => {
  const [tableConfigStorageKey, filterConfigStorageKey] = useStorageKey(
    ["table-configuration", "filter-configuration"],
    [assetType, "emissions", "records"]
  );

  const { assetFilter, linkToEmissionRecordDetail } = ((): {
    assetFilter: Record<string, string | undefined>;
    linkToEmissionRecordDetail?: (id: string, period: string) => string;
  } => {
    switch (assetType) {
      case AssetType.FACILITY:
        return {
          assetFilter: { "equipment.facility.id": assetId },
          linkToEmissionRecordDetail: linkToFacilityEmissionRecordDetail,
        };
      case AssetType.EQUIPMENT:
        return {
          assetFilter: { entity_id: assetId, entity_type: assetType },
          linkToEmissionRecordDetail: linkToEquipmentEmissionRecordDetail,
        };
      default:
        return {
          assetFilter: {},
        };
    }
  })();

  const [filterPanelFilters] = useFilters<any>(filterConfigStorageKey);

  const dateRange = filterPanelFilters[FILTER_CONFIG.date.name];
  const reportingGroup =
    filterPanelFilters[FILTER_CONFIG.reportingScenario.name];

  const calculatorResultFilters = {
    ...assetFilter,
    ...(reportingGroup ? { reporting_group: { $exact: reportingGroup } } : {}),
    year_month: getIntervalInMonthsFormatted(
      dateRange
        ? hydrateDateRange(dateRange)
        : DEFAULT_DATE_RANGES.lastTwelveMonths
    ),
  };

  const [tableStateStorageKey] = useStorageKey(
    ["table-state"],
    "emissions-records"
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "year_month",
      sortDirection: SortDirection.ASCENDING,
    },
    calculatorResultFilters,
    tableStateStorageKey
  );

  const isQueryEnabled = !isNil(reportingGroup);

  const summaryQuery = useListCalculatorResults(
    {
      page: 1,
      pageSize: 1,
      pageDisableTotal: true,
      // @ts-expect-error TODO: Typing on filter
      filters: calculatorResultFilters,
    },
    { enabled: isQueryEnabled }
  );
  const summaryItems = summaryQuery.data?.data ?? [];

  const tableQuery = useListCalculatorResults({
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    // @ts-expect-error TODO: Typing on filter
    filters: calculatorResultFilters,
    groupBy: ["year_month"],
  });
  const {
    isLoading: isLoadingMeasurementHeaders,
    headers: measurementHeaders,
  } = useCalculatorResultsMeasurementHeaders(summaryItems);
  const items = tableQuery.data?.data ?? [];
  const headers = [
    {
      label: "Time Period",
      key: "year_month",
      isSortable: true,
      renderComponent: ({
        item: { year_month: yearMonth },
      }: {
        item: CalculatorResultType;
      }) =>
        yearMonth && assetId && linkToEmissionRecordDetail ? (
          <RoutingLink to={linkToEmissionRecordDetail?.(assetId, yearMonth)}>
            {monthFormatter(
              parse(yearMonth, DateFormats.YEAR_MONTH, new Date())
            )}
          </RoutingLink>
        ) : (
          "-"
        ),
    },
    ...measurementHeaders,
  ];

  return (
    <>
      <FacilityUpdateStatusBanner />
      <EquipmentUpdateStatusBanner />
      <FilterPanel
        storageKey={filterConfigStorageKey}
        defaultValue={{
          [FILTER_CONFIG.date.name]: DEFAULT_DATE_RANGES.lastTwelveMonths,
        }}
        filters={[
          {
            component: (
              <ReportingGroupFilterSwitcher
                key={FILTER_CONFIG.reportingScenario.name}
                name={FILTER_CONFIG.reportingScenario.name}
                shouldSelectFirstOptionAsDefault
              />
            ),
          },
          {
            component: (
              <DateSelectorInput
                key={FILTER_CONFIG.date.name}
                name={FILTER_CONFIG.date.name}
                variant="month"
                isRange
                isInline
              />
            ),
          },
        ]}
      />
      <CalculatorResultsSummaryPanel
        // only the first result matters since this is an aggregate request with no group by
        data={summaryItems?.[0] ?? null}
        isLoading={isQueryEnabled && summaryQuery.isLoading}
        loadingGridSize={
          items.reduce(
            (max, row) => Math.max(max, Object.keys(row.measurement).length),
            6
          ) ?? null
        }
        hasError={summaryQuery.isError}
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{ title: "Records", ariaLabel: "Records Panel" }}
        dataTableProps={{
          headers,
          items,
          getItemId: (item) =>
            [item.entity_id, item.year_month, item.reporting_group].join("-"),
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          isLoading:
            tableQuery.isLoading ||
            summaryQuery.isLoading ||
            isLoadingMeasurementHeaders,
          isBusy: tableQuery.isFetching,
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: tableQuery.data?.total_entries,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
        }}
      />
    </>
  );
};
